import { Component, inject, OnInit } from '@angular/core';
import { FONT_AWESOME_ICONS } from '@ep/shared';
import { AsyncPipe, CurrencyPipe, DatePipe, NgIf } from '@angular/common';
import { ReportsApiService } from '../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { SdkBatchDetail } from '@app/resources/services/batch.service';
import { Observable, of, switchMap, catchError, map, take, shareReplay } from 'rxjs';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TableModule } from 'primeng/table';
import { PrimeTemplate } from 'primeng/api';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule } from '@angular/forms';
import { getOrderDateUtc } from '@app/areas/transactions/utils/mappers';
import { ApplicationProfile, PortalTheme } from '@app/@shared/models/application-profile.model';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { BreadcrumbService } from '@app/resources/services/breadcrumb.service';
import { FA_ICON } from '@shared/icons';

@Component({
  selector: 'app-existing-sdk-batch-report-detail',
  standalone: true,
  imports: [
    AsyncPipe,
    FaIconComponent,
    DatePipe,
    NgIf,
    CurrencyPipe,
    TableModule,
    PrimeTemplate,
    InputSwitchModule,
    FormsModule,
  ],
  template: `
    <div class="p-4">
      <p-table
        [value]="(batchDetail$ | async) ?? []"
        styleClass="p-datatable-striped"
        [tableStyle]="{ 'min-width': '50rem' }"
        [paginator]="true"
        [rows]="10"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        [rowsPerPageOptions]="[5, 10, 20]"
        data-cy="batches-detail-table"
      >
        <ng-template pTemplate="caption">
          <div class="flex align-items-center justify-content-between">
            <div class="flex align-items-center gap-3">
              <h2>BATCH DETAILS</h2>
            </div>

            <div class="align-items-center d-flex">
              <div class="export-button">
                <button
                  type="button"
                  class="p-button p-button-sm p-button-outlined"
                  (click)="exportToCSV()"
                  data-cy="export-csv-button"
                >
                  <fa-icon [icon]="icons['faFileExport']" class="mr-2" />
                  Export to CSV
                </button>
              </div>
              <div class="export-button">
                <button
                  type="button"
                  class="p-button p-button-sm p-button-outlined"
                  (click)="exportToPDF()"
                  data-cy="export-pdf-button"
                >
                  <fa-icon [icon]="icons['faFileExport']" class="mr-2" />
                  Export to PDF
                </button>
              </div>
            </div>
          </div>
        </ng-template>
        <ng-template pTemplate="header">
          <tr>
            <th>Date</th>
            <th>Subtotal</th>
            <th *ngIf="showTaxAmount">
              Tax Amount <br />
              <span class="fa-2xs">(Calculated after discounts)</span>
            </th>
            <th>Total Amount</th>
            <th>Discounts</th>
            <th>Amount Collected</th>
            <th *ngIf="showTipAmount">Tip Amount</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-detail>
          <tr>
            <td>{{ getTransactionDateUtc(detail.UpdateDateTime) | date : 'MM/dd/YY h:mm a' }}</td>
            <td>{{ detail.SubTotal | currency }}</td>
            <td *ngIf="showTaxAmount">{{ detail.AmountTax | currency }}</td>
            <td>{{ detail.AmountTotal | currency }}</td>
            <td>{{ detail.RewardAndDiscount | currency }}</td>
            <td>{{ detail.AmountCollected | currency }}</td>
            <td *ngIf="showTipAmount">{{ detail.AmountGratuity | currency }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="footer">
          <tr *ngIf="batchSummary$ | async as summary" class="bg-gray-50">
            <td>
              <div class="font-bold">Date</div>
              <div>{{ getTransactionDateUtc(displayBatchFirstDate!) | date : 'MM/dd/YY' }}</div>
            </td>
            <td>
              <div class="font-bold">Subtotal</div>
              <div>{{ summary.totalSubTotal | currency }}</div>
            </td>
            <td *ngIf="showTaxAmount">
              <div class="font-bold">Total Tax</div>
              <div>{{ summary.totalTax | currency }}</div>
            </td>
            <td>
              <div class="font-bold">POS Total</div>
              <div>{{ summary.totalAmount | currency }}</div>
            </td>
            <td>
              <div class="font-bold">Total Discounts</div>
              <div>{{ summary.totalRewardAndDiscount | currency }}</div>
            </td>
            <td>
              <div class="font-bold">Batch Total</div>
              <div>{{ summary.totalAmountCollected | currency }}</div>
            </td>
            <td *ngIf="showTipAmount">
              <div class="font-bold">Total Tips</div>
              <div>{{ summary.totalGratuity | currency }}</div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  `,
  styleUrl: './existing-sdk-batch-report-detail.component.scss',
})
export class ExistingSdkBatchReportDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly reportService = inject(ReportsApiService);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  icons = FONT_AWESOME_ICONS;

  protected batchId: string | undefined = this.route.snapshot.url.map((segment) => segment.path).pop();
  protected displayBatchId: string | undefined;
  protected displayBatchFirstDate: Date | null = null;

  protected batchDetails: SdkBatchDetail[] = [];
  protected batchDetail$: Observable<SdkBatchDetail[]> = of([]);
  protected batchSummary$: Observable<{
    totalAmount: number;
    totalTax: number;
    totalGratuity: number;
    totalRewardAndDiscount: number;
    totalAmountCollected?: number;
    totalSubTotal?: number;
  }> = of({
    totalAmount: 0,
    totalTax: 0,
    totalGratuity: 0,
    totalRewardAndDiscount: 0,
    totalAmountCollected: 0,
    totalSubTotal: 0,
  });
  protected locationUtcOffsetHour: number = 0;
  protected theme: PortalTheme | undefined;
  protected showTaxAmount: boolean = false;
  protected showTipAmount: boolean = false;

  public ngOnInit(): void {
    this.applicationProfileService.applicationProfile$.pipe().subscribe((applicationProfile: ApplicationProfile) => {
      this.locationUtcOffsetHour = applicationProfile.Location.UtcOffsetHour;
      this.theme = applicationProfile.PortalTheme.Theme;
      this.setCSSCustomProperties();
    });

    this.breadcrumbService.setPageBreadcrumbs([
      { label: 'Reports', icon: FA_ICON.FILE_EXPORT, command: () => this.navigateBackToReports() },
      { label: 'Batch Reports', command: () => this.navigateBack() },
      { label: `Batch Id ${this.batchId}` },
    ]);

    if (this.batchId) {
      this.displayBatchId = this.batchId;
      this.batchDetail$ = this.reportService.getSdkBatchById(this.batchId).pipe(
        catchError((error) => {
          console.error('Error fetching batch details:', error);
          return of([]);
        }),
        map((details) => {
          this.batchDetails = details;

          // Check if any batch detail has a tax amount greater than 0
          this.showTaxAmount = this.batchDetails.some((detail) => Number(detail.AmountTax) > 0);

          // Check if any batch detail has a tip amount greater than 0
          this.showTipAmount = this.batchDetails.some((detail) => Number(detail.AmountGratuity) > 0);

          if (this.batchDetails.length > 0) {
            const firstBatchDate = this.batchDetails.reduce((earliest, detail) => {
              const detailDate = detail.UpdateDateTime ? new Date(detail.UpdateDateTime) : new Date();
              return detailDate < earliest ? detailDate : earliest;
            }, new Date(this.batchDetails[0].UpdateDateTime || new Date()));
            this.displayBatchFirstDate = firstBatchDate;
          }

          return details;
        }),
        shareReplay(1)
      );

      this.batchSummary$ = this.batchDetail$.pipe(
        map((details) => {
          return details.reduce(
            (acc, detail) => {
              acc.totalAmount += Number(detail.AmountTotal) || 0;
              acc.totalTax += Number(detail.AmountTax) || 0;
              acc.totalGratuity += Number(detail.AmountGratuity) || 0;
              acc.totalRewardAndDiscount += Number(detail.RewardAndDiscount) || 0;
              acc.totalAmountCollected += Number(detail.AmountCollected) || 0;
              acc.totalSubTotal += Number(detail.SubTotal) || 0;
              return acc;
            },
            {
              totalAmount: 0,
              totalTax: 0,
              totalGratuity: 0,
              totalRewardAndDiscount: 0,
              totalAmountCollected: 0,
              totalSubTotal: 0,
            }
          );
        })
      );
    }
  }

  protected location$: Observable<number> = this.applicationProfileService.applicationProfile$.pipe(
    switchMap((appProfile) => {
      return of(appProfile.Location.LocationId);
    })
  );

  public navigateBack(): void {
    this.location$.subscribe((location) => {
      this.router.navigate([`${location}/reports/SdkBatchReport`]);
    });
  }

  public navigateBackToReports(): void {
    this.location$.subscribe((location) => {
      this.router.navigate([`${location}/reports`]);
    });
  }

  protected getTransactionDateUtc(originalDate: Date): string {
    return getOrderDateUtc(originalDate, this.locationUtcOffsetHour);
  }

  public exportToCSV(): void {
    this.batchDetail$.subscribe((details) => {
      if (details.length === 0) {
        return;
      }

      // Define CSV headers
      const headers = [
        'Date',
        'Subtotal',
        'Tax Amount',
        'Total Amount',
        'Discounts',
        'Amount Collected',
        'Tips Amount',
      ];

      // Convert data to CSV rows
      const rows = details.map((detail) => {
        const baseRow = [this.formatDateForCSV(detail.UpdateDateTime), this.formatCurrency(detail.SubTotal)];

        baseRow.push(
          this.formatCurrency(detail.AmountTax),
          this.formatCurrency(detail.AmountTotal),
          this.formatCurrency(detail.RewardAndDiscount),
          this.formatCurrency(detail.AmountCollected),
          this.formatCurrency(detail.AmountGratuity)
        );

        return baseRow;
      });

      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `batch_${this.displayBatchId}_${this.getCurrentDateForFilename()}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  }

  public async exportToPDF(): Promise<void> {
    // Dynamically import jsPDF and autoTable
    const { default: jsPDF } = await import('jspdf');
    await import('jspdf-autotable');

    this.batchDetail$.subscribe((details) => {
      if (details.length === 0) {
        return;
      }

      // Create a new PDF document
      const doc = new jsPDF() as any;

      // Add title
      doc.setFontSize(16);
      doc.text(`Batch Report - Batch ID: ${this.displayBatchId}`, 14, 15);

      // Add batch date
      if (this.displayBatchFirstDate) {
        doc.setFontSize(10);
        const formattedDate = this.formatDateForCSV(this.displayBatchFirstDate);
        doc.text(`Batch Date: ${formattedDate.split(' ')[0]}`, 14, 22);
      }

      // Define table headers
      const headers = [
        ['Date', 'Subtotal', 'Tax Amount', 'Total Amount', 'Discounts', 'Amount Collected', 'Tips Amount'],
      ];

      // Prepare table data
      const tableData = details.map((detail) => {
        const baseRow = [this.formatDateForCSV(detail.UpdateDateTime), `$${this.formatCurrency(detail.SubTotal)}`];

        baseRow.push(
          `$${this.formatCurrency(detail.AmountTax)}`,
          `$${this.formatCurrency(detail.AmountTotal)}`,
          `$${this.formatCurrency(detail.RewardAndDiscount)}`,
          `$${this.formatCurrency(detail.AmountCollected)}`,
          `$${this.formatCurrency(detail.AmountGratuity)}`
        );

        return baseRow;
      });

      // Calculate totals
      const summary = details.reduce(
        (acc, detail) => {
          acc.totalSubTotal += Number(detail.SubTotal) || 0;
          acc.totalTax += Number(detail.AmountTax) || 0;
          acc.totalGratuity += Number(detail.AmountGratuity) || 0;
          acc.totalAmount += Number(detail.AmountTotal) || 0;
          acc.totalRewardAndDiscount += Number(detail.RewardAndDiscount) || 0;
          acc.totalAmountCollected += Number(detail.AmountCollected) || 0;
          return acc;
        },
        {
          totalSubTotal: 0,
          totalTax: 0,
          totalGratuity: 0,
          totalAmount: 0,
          totalRewardAndDiscount: 0,
          totalAmountCollected: 0,
        }
      );

      // Add totals row
      const totalsRow = ['Total', `$${summary.totalSubTotal.toFixed(2)}`];

      totalsRow.push(
        `$${summary.totalTax.toFixed(2)}`,
        `$${summary.totalAmount.toFixed(2)}`,
        `$${summary.totalRewardAndDiscount.toFixed(2)}`,
        `$${summary.totalAmountCollected.toFixed(2)}`,
        `$${summary.totalGratuity.toFixed(2)}`
      );

      tableData.push(totalsRow);

      // Generate table using autoTable
      doc.autoTable({
        head: headers,
        body: tableData,
        startY: 28,
        theme: 'striped',
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 22 },
          2: { cellWidth: 22 },
          3: { cellWidth: 28 },
          4: { cellWidth: 28 },
          5: { cellWidth: 32 },
          6: { cellWidth: 26 },
        },
        didParseCell: function (data: any) {
          // Style the total row
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [240, 240, 240];
          }
        },
      });

      // Save the PDF
      doc.save(`batch_${this.displayBatchId}_${this.getCurrentDateForFilename()}.pdf`);
    });
  }

  private formatDateForCSV(date: Date | undefined): string {
    if (!date) return '';
    const dateStr = this.getTransactionDateUtc(date);
    const dateObj = new Date(dateStr);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = String(dateObj.getFullYear()).slice(-2);
    const hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}/${day}/${year} ${displayHours}:${minutes} ${ampm}`;
  }

  private formatCurrency(value: number | string | undefined): string {
    if (value === undefined || value === null) return '0.00';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return numValue.toFixed(2);
  }

  private getCurrentDateForFilename(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties

      document.documentElement.style.setProperty('--table-bg-primary', this.theme.table.header.backgroundColor);

      document.documentElement.style.setProperty(
        '--custom-header-background',
        this.theme?.table.header.backgroundColor
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-background',
        this.theme?.table.subHeaderBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-text-color',
        this.theme?.table.subHeaderTextColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-text-color',
        this.theme?.table.header.textColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-primary-background',
        this.theme?.table.body.primaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-secondary-background',
        this.theme?.table.body.secondaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-text-color',
        this.theme?.table.body.textColor || ''
      );

      // // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('ExistingBatchReportComponent');
    }
  }
}
